/**
 * Extracts a user-friendly error message from a GraphQL error object.
 *
 * If the error message contains a JSON payload with a 'response.errors' array,
 * it parses and returns a comma-separated string of error messages. Otherwise,
 * it returns the original error message.
 *
 * @param error - The error object thrown by a GraphQL request
 * @returns A string containing the extracted or original error message(s)
 */
export const extractGQLErrorMessage = (error: Error): string => {
  const { message } = error
  const separatorLocation = message.indexOf(':')

  if (separatorLocation === -1) {
    return message
  }

  const payload = message.slice(separatorLocation + 1)?.trim()
  if (!payload) {
    return message
  }

  try {
    const info: unknown = JSON.parse(payload)

    if (!(info && typeof info === 'object' && 'response' in info)) {
      return message
    }

    const { response } = info

    if (!(response && typeof response === 'object' && 'errors' in response)) {
      return message
    }

    const { errors } = response

    if (!(Array.isArray(errors) && errors.length)) {
      return message
    }

    return errors
      .map((err: unknown) => (err && typeof err === 'object' && 'message' in err ? err.message : 'Unknown error'))
      .join(', ')
  } catch (parseError) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse GraphQL error payload:', parseError)
    return message
  }
}
